import React, { FC, useEffect, useState } from "react";
import { useSelector, RootStateOrAny } from "react-redux";
import { Link } from "react-router-dom";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Tooltip,
} from "antd";
import moment from "moment";
import { omit } from "lodash";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import "./DocumentTemplates.less";
import { DictionaryDto } from "../../lib/interfaces/DictionaryDto";
import ChangeVersionsTable from "../common/ChangeVersionsTable";
import MultipleSelect from "../common/MultipleSelect";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ADMIN_REPORT_TEMPLATE } from "../../lib/const/permissions";
import DownloadFile from "../../img/DownloadFile";
import { displayFormat, toHttpFormat, toHttpFormatEndOfDay } from "../../lib/utils/formatDate";
import UploadFileDialog from "./UploadFileDialog";
import {
  CloseDocumentTemplate,
  DocumentTemplate,
  DownloadDocumentTemplateFile,
  UpdateDocumentTemplate,
} from "../../lib/interfaces/DocumentTemplate";
import { checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { IDocumentTemplates } from "../../store/slices/documentTemplates";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";

interface IProps {
  reportType: DictionaryDto[];
  acceptedFileTypes: string[];
  searchTemplates: (props: { onDate?: string; reportTypeId?: number }) => void;
  getTemplateVersions: (reportId: number) => void;
  resetTemplateVersions: () => void;
  downloadDocumentTemplateFile: DownloadDocumentTemplateFile;
  updateDocumentTemplate: UpdateDocumentTemplate;
  closeDocumentTemplate: CloseDocumentTemplate;
}

const DocumentTemplateList = ({
  reportType,
  acceptedFileTypes,
  searchTemplates,
  getTemplateVersions,
  resetTemplateVersions,
  downloadDocumentTemplateFile,
  updateDocumentTemplate,
  closeDocumentTemplate,
}: IProps) => {
  const {
    templates,
    templatesLoading,
    templateVersions,
    templateVersionsLoading,
  }: IDocumentTemplates = useSelector((state: RootStateOrAny) => state.documentTemplates);

  const [formSearchFilter] = Form.useForm();

  const [documentForm] = Form.useForm();

  const [childTableData, setChildTableData] = useState<any>([]);
  const emptyHistoryObject = { id: 0, fromDate: "", toDate: "" };
  const [historyObject, setHistoryObject] = useState<{
    id: number;
    fromDate: string;
    toDate: string;
    [key: string]: string | number;
  }>(emptyHistoryObject);

  // const emptyUploadedFile = { file: null, changed: false };

  // const [uploadedFile, setUploadedFile] = useState<{
  //   file: UploadedTemplateFile;
  //   changed: boolean;
  // }>(emptyUploadedFile);

  const [filterValues, setFilterValues] = useState<{
    ids: number[];
    name: string;
    reportTypeIds: number[];
  }>({ ids: [], name: "", reportTypeIds: [] });

  const handleSetFilterValues = (field) => {
    setFilterValues((prev) => ({ ...prev, ...field }));
  };

  const [tableData, setTableData] = useState<any[]>([]);

  useEffect(() => {
    const data = templates
      .map((document) => ({ ...document, key: document.id }))
      .sort(versionsTableUtils.sortVersionsByDate)
      .filter(({ id }) => {
        if (filterValues.ids.length > 0) {
          return filterValues.ids.some((selectedId) => String(id).startsWith(String(selectedId)));
        }
        return true;
      })
      .filter(({ name }) => {
        if (filterValues.name) {
          return name.toLowerCase().includes(filterValues.name);
        }
        return true;
      })
      .filter(({ reportTypeId }) => {
        if (filterValues.reportTypeIds.length > 0) {
          return filterValues.reportTypeIds.includes(reportTypeId);
        }
        return true;
      });
    setTableData(data);
  }, [templates, filterValues]);

  useEffect(() => {
    setChildTableData(
      templateVersions
        .map((version, i) => ({
          ...version,
          ...versionsTableUtils.getCommonChildTableProps(version, i, templateVersions.length),
        }))
        .sort(versionsTableUtils.sortVersionsByDate)
    );
  }, [templateVersions]);

  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);

  const renderIdTitle = () => {
    const notValidRgx = /[^0-9,]/;
    const onChangeId = ({ target: { value } }) => {
      if (!notValidRgx.test(value)) {
        const ids = value?.split(/\D/).filter(Boolean).map(Number);
        handleSetFilterValues({ ids });
      }
    };

    const validator = (x, value) => {
      if (value && notValidRgx.test(value)) {
        return Promise.reject();
      }
      return Promise.resolve();
    };

    return (
      <Tooltip title="Введите ID документов через ','">
        <Form.Item
          name="ids"
          style={{ marginBottom: 0 }}
          className="form-item-required__hidden-error__mb0"
          rules={[
            {
              validator,
            },
          ]}
        >
          <Input placeholder="ID" allowClear onChange={onChangeId} />
        </Form.Item>
      </Tooltip>
    );
  };

  renderIdTitle.toString = () => "ID документа";

  const renderNameTitle = () => {
    const onChangeName = ({ target: { value } }) => {
      handleSetFilterValues({ name: value.toLowerCase().trim() });
    };

    return <Input placeholder="Наименование" allowClear onChange={onChangeName} />;
  };

  renderNameTitle.toString = () => "Наименование";

  const renderReportTypeTitle = () => {
    const onChangeReportTypeId = (reportTypeIds) => {
      handleSetFilterValues({ reportTypeIds });
    };

    return (
      <MultipleSelect
        placeholder="Тип"
        allowClear
        onChange={onChangeReportTypeId}
        allValues={reportType.map(({ id }) => id)}
        showArrow
        showSearch={false}
        value={filterValues.reportTypeIds}
        maxTagCount={1}
      >
        {reportType.map((item) => (
          <Select.Option key={item.id} value={item.id}>
            {item.name}
          </Select.Option>
        ))}
      </MultipleSelect>
    );
  };

  renderReportTypeTitle.toString = () => "Тип";

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    id: "100px",
    defaultTemplate: "35px",
    actions: "180px",
    name: "",
    reportTypeId: "270px",
    dsc: "270px",
  };

  const defaultTemplateTitle = () => "";
  defaultTemplateTitle.toString = () => "Шаблон по умолчанию";

  const getColumns = () => [
    {
      title: renderIdTitle,
      dataIndex: "id",
      key: "id",
      width: columnsWidths.id,
      hidden: true,
    },
    {
      title: defaultTemplateTitle,
      key: "defaultTemplate",
      width: columnsWidths.defaultTemplate,
      dataIndex: "defaultTemplate",
      hidden: false,
      render: (defaultTemplate) =>
        defaultTemplate ? (
          <Tooltip title="Шаблон по умолчанию">
            <Checkbox disabled checked />
          </Tooltip>
        ) : null,
    },
    {
      title: renderNameTitle,
      dataIndex: "name",
      key: "name",
      width: columnsWidths.name,
      hidden: false,
    },
    {
      title: renderReportTypeTitle,
      dataIndex: "reportTypeId",
      key: "reportTypeId",
      width: columnsWidths.reportTypeId,
      hidden: false,
      render: (reportTypeId) => versionsTableUtils.geDicValueById(reportType, reportTypeId)?.name,
    },
    {
      title: "Комментарий",
      dataIndex: "dsc",
      key: "dsc",
      width: columnsWidths.dsc,
      hidden: false,
    },
    {
      title: "Период",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={documentForm.setFieldsValue}
          fromDateRequired
          toDateRequired
        />
      ),
    },
    {
      key: "actions",
      width: columnsWidths.actions,
    },
    {
      key: "buttons",
      width: columnsWidths.buttons,
      render: (record) => (
        <DotsIcon
          style={{ cursor: "pointer" }}
          onClick={() => {
            setHistoryObject(record);
          }}
        />
      ),
    },
  ];

  const getChildColumns = () => [
    {
      title: renderIdTitle,
      dataIndex: "id",
      key: "id",
      width: columnsWidths.id,
      hidden: true,
    },
    {
      title: defaultTemplateTitle,
      key: "defaultTemplate",
      width: columnsWidths.defaultTemplate,
      dataIndex: "defaultTemplate",
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Checkbox
          name="defaultTemplate"
          record={record}
          setFieldsValue={documentForm.setFieldsValue}
          tooltipTitle="Шаблон по умолчанию"
        />
      ),
    },
    {
      title: renderNameTitle,
      dataIndex: "name",
      key: "name",
      width: columnsWidths.name,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="name"
          record={record}
          setFieldsValue={documentForm.setFieldsValue}
          required
        />
      ),
    },
    {
      title: renderReportTypeTitle,
      dataIndex: "reportTypeId",
      key: "reportTypeId",
      width: columnsWidths.reportTypeId,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Select
          dic={reportType}
          name="reportTypeId"
          record={record}
          setFieldsValue={documentForm.setFieldsValue}
          required
          presetAddVerionValue
        />
      ),
    },
    {
      title: "Комментарий",
      dataIndex: "dsc",
      key: "dsc",
      width: columnsWidths.dsc,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="dsc"
          record={record}
          setFieldsValue={documentForm.setFieldsValue}
        />
      ),
    },
    {
      title: "Период",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={documentForm.setFieldsValue}
          fromDateRequired
          toDateRequired
        />
      ),
    },
    {
      key: "actions",
      width: columnsWidths.actions,
      render: (v, record) => {
        const isHasAccess = hasAccess(MENU_ADMIN_REPORT_TEMPLATE);
        const isRecordCreating = versionsTableUtils.getIsRecordCreating(record);
        const isRecordEditing = versionsTableUtils.getIsRecordEditing(record);
        const isRecordChanging = versionsTableUtils.getIsRecordChanging(record);
        const isRecordClosing = versionsTableUtils.getIsRecordClosing(record);
        return (
          <div style={{ display: "flex" }}>
            {!isRecordChanging && isHasAccess && (
              <DownloadFile
                onClick={() => {
                  downloadDocumentTemplateFile({ reportId: record.id, onDate: record.fromDate });
                }}
                disabled={isChangingForbidden || !record.isTemplate}
                tooltipTitle={record.isTemplate ? "Скачать" : "Файл шаблона отсутствует"}
                style={{ top: "5px" }}
              />
            )}
            <ChangeVersionsTable.AddButton
              hasAccess={isHasAccess}
              record={record}
              isChangingForbidden={isChangingForbidden}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.EditButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
              onClick={() => {
                documentForm.setFieldsValue({
                  isTemplate: record.isTemplate,
                });
              }}
            />
            <ChangeVersionsTable.CloseButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            {(isRecordCreating || isRecordEditing) && (
              <Form.Item shouldUpdate className="form-item-required__hidden-error__mb0">
                {({ getFieldValue, setFieldsValue }) => {
                  const fileChanged = getFieldValue("reportTemplate");
                  const fileExists = getFieldValue("isTemplate");
                  return (
                    <Form.Item
                      name="reportTemplate"
                      className="form-item-required__hidden-error__mb0"
                      rules={[{ required: !fileExists }]}
                    >
                      <UploadFileDialog
                        acceptedFileTypes={acceptedFileTypes}
                        fileExists={Boolean(fileExists)}
                        fileChanged={fileChanged}
                        setFile={(file) => {
                          setFieldsValue({
                            reportTemplate: file,
                            isTemplate: Boolean(file),
                          });
                          if (file && fileExists) {
                            Modal.info({
                              content: "Файл шаблона будет перезаписан",
                            });
                          }
                        }}
                        fileChangedTitle="Файл заменен"
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            )}
            <Form.Item shouldUpdate className="form-item-required__hidden-error__mb0">
              {({ getFieldValue }) => {
                const fileExists = getFieldValue("isTemplate");
                const disabled = (() => {
                  if (isRecordClosing || fileExists) return undefined;
                  return {
                    state: true,
                    title: "Необходимо загрузить файл",
                  };
                })();
                return (
                  <ChangeVersionsTable.SubmitButton
                    record={record}
                    submit={documentForm.submit}
                    disabled={disabled}
                  />
                );
              }}
            </Form.Item>
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={() => {
                documentForm.resetFields();
                // setUploadedFile(emptyUploadedFile);
              }}
              setChildTableData={setChildTableData}
            />
          </div>
        );
      },
    },
    {
      key: "buttons",
      width: columnsWidths.buttons,
      render: (record) => (
        <DotsIcon
          style={{ cursor: "pointer" }}
          onClick={() => {
            setHistoryObject(record);
          }}
        />
      ),
    },
  ];

  const [columns, setColumns] = useState(getColumns());

  useEffect(() => setColumns(getColumns()), [reportType]);

  const [childColumns, setChildColumns] = versionsTableUtils.useChildColumns(getChildColumns, [
    isChangingForbidden,
    tableData,
    reportType,
    // uploadedFile,
  ]);

  const onFinish = () => {
    documentForm.validateFields().then((values: DocumentTemplate) => {
      const changeableRecord = versionsTableUtils.getChangeableRecord(childTableData);
      const isRecordEditing = versionsTableUtils.getIsRecordEditing(changeableRecord);
      const isRecordClosing = versionsTableUtils.getIsRecordClosing(changeableRecord);
      if (isRecordClosing) {
        const onDate = formSearchFilter.getFieldValue("onDate");

        closeDocumentTemplate({
          reportId: changeableRecord.id,
          fromDate: toHttpFormatEndOfDay(values.toDate),
          onDate,
        });
      } else {
        const updatedTemplate: DocumentTemplate = {
          ...omit(values, "ids"),
          id: changeableRecord.id,
          fromDate: toHttpFormat(isRecordEditing ? changeableRecord.fromDate : values.fromDate),
          toDate: toHttpFormat(changeableRecord.toDate),
        };
        // console.log(updatedTemplate);
        updateDocumentTemplate({
          reportId: updatedTemplate.id,
          fromDate: updatedTemplate.fromDate,
          force: isRecordEditing,
          updatedTemplate,
        }).then(() => documentForm.resetFields());
      }
    });
  };

  return (
    <>
      <Form
        form={formSearchFilter}
        style={{ marginLeft: "10px", marginRight: "20px" }}
        onFinish={({ onDate }) => {
          searchTemplates(onDate ? { onDate: toHttpFormat(onDate) } : {});
        }}
      >
        <Row justify="space-between">
          <Col>
            <Row gutter={20}>
              <Col>
                <Form.Item label="Дата" name="onDate" initialValue={moment()}>
                  <DatePicker format={displayFormat} allowClear={false} />
                </Form.Item>
              </Col>
              <Col>
                <Button htmlType="submit" type="primary">
                  Применить
                </Button>
              </Col>
            </Row>
          </Col>
          <Col>
            {hasAccess(MENU_ADMIN_REPORT_TEMPLATE) && (
              <Button type="primary">
                <Link to="document-templates/upload">+ Добавить шаблон</Link>
              </Button>
            )}
          </Col>
        </Row>
      </Form>
      <Divider style={{ margin: "0" }} />
      <Form form={documentForm} onFinish={onFinish}>
        <ToggleableVersionsTable
          tableName="document-templates"
          columns={columns}
          setColumns={setColumns}
          childTableColumns={childColumns}
          setChildTableColumns={setChildColumns}
          tableData={tableData}
          childTableData={childTableData}
          isTableLoading={checkIsLoading(templatesLoading)}
          isChildTableLoading={checkIsLoading(templateVersionsLoading)}
          onOpenVersionsCallback={({ id }) => {
            getTemplateVersions(id);
          }}
          onCloseVersionsCallback={() => {
            resetTemplateVersions();
            // setUploadedFile(emptyUploadedFile);
            documentForm.resetFields();
          }}
          resetExpandOnTableDataChange
        />
      </Form>
      <HistoryAndPropertyModal
        objectTypeSysname="report_template"
        objectId={historyObject?.id}
        handleHideModal={() => setHistoryObject(emptyHistoryObject)}
        visibleModal={Boolean(historyObject?.id)}
        dateObjectFromForCalendar={historyObject?.fromDate}
        dateObjectToForCalendar={historyObject?.toDate}
        // hasAccessToEdit={hasAccess(MENU_ACCOUNT_AGREEMENT_WRITE)}
        hasAccessToEdit={false}
      />
    </>
  );
};

export default DocumentTemplateList;
